import { IMAGES } from "../lib/site";
import CTASection from "../components/site/CTASection";
import { Feather, Award, Heart } from "lucide-react";

export default function Story() {
  return (
    <>
      {/* Cinematic hero */}
      <section
        className="relative w-full h-[85vh] min-h-[560px] overflow-hidden bg-black"
        data-testid="story-hero"
      >
        <img
          src={IMAGES.reeds}
          alt="Norfolk reedbeds"
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/70" />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-20 md:pb-28">
          <p className="overline text-white/80 mb-6 flex items-center gap-3">
            <Feather size={16} /> Our story
          </p>          <h1 className="font-serif-display text-white text-6xl sm:text-7xl lg:text-8xl leading-[0.9] max-w-4xl">
            Brought back through
            <span className="block italic">care and hard work.</span>
          </h1>
        </div>
      </section>

      {/* Narrative */}
      <section className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 fade-up">
        <p className="overline text-neutral-500 mb-8">Where the name came from</p>
        <div className="prose-none space-y-8 text-neutral-800 text-lg sm:text-xl leading-[1.7] font-serif-display font-normal">
          <p>
            The Bittern is one of Norfolk&rsquo;s most iconic and distinctive birds,
            deeply connected to the reedbeds and wetlands of the Norfolk Broads.
          </p>
          <p>
            Once close to extinction in the UK, the Bittern made a remarkable
            comeback through years of care, restoration, and ongoing
            maintenance of its natural habitat.
          </p>
          <p>
            At Bittern Property Services, that story reflects our own values
            perfectly. Just as the Bittern was brought back through dedication
            and hard work, our aim is to restore, maintain, and protect the
            homes and properties we work on across Norfolk.
          </p>
          <p>
            Whether it&rsquo;s improving a garden, repairing a property, or helping
            bring a space back to its best, we take pride in delivering
            reliable workmanship built on care, resilience, and strong local
            roots.
          </p>
        </div>
      </section>

      {/* Editorial split */}
      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 fade-up">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-stretch">
          <div className="md:col-span-6 img-hover">
            <img
              src={IMAGES.wetlands}
              alt="Norfolk wetlands"
              className="w-full h-full min-h-[420px] object-cover"
            />
          </div>
          <div className="md:col-span-6 bg-neutral-100 p-10 md:p-14 flex flex-col justify-center">
            <Award size={28} strokeWidth={1.5} />
            <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl leading-tight">
              Fifteen years on the tools. Countless jobs completed.
            </h2>
            <p className="mt-6 text-neutral-600 leading-relaxed text-lg">
              Bittern Property Services brings together over 15 years of
              hands-on trade experience — building the kind of quiet, reliable
              reputation that keeps a phone ringing on word of mouth alone.
              Every job is our reference for the next one.
            </p>
          </div>
        </div>
      </section>

      {/* Conservation promise */}
      <section
        className="bg-neutral-950 text-white"
        data-testid="conservation-section"
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 fade-up">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7">
              <p className="overline text-neutral-400 mb-6 flex items-center gap-3">
                <Heart size={16} /> Our promise
              </p>
              <h2 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                10% of our profits go back
                <span className="block italic text-neutral-400">
                  to Norfolk wildlife conservation.
                </span>
              </h2>
              <p className="mt-8 text-neutral-300 text-lg leading-relaxed max-w-2xl">
                Every job you book quietly funds local conservation work —
                protecting the reedbeds, wetlands and species that give this
                county its character. It&rsquo;s a small thank-you to the place that
                made us.
              </p>
            </div>
            <div className="md:col-span-5">
              <div className="border border-white/20 p-10">
                <p className="font-serif-display text-8xl leading-none">10%</p>
                <p className="mt-4 text-[11px] tracking-[0.22em] uppercase text-neutral-400">
                  of profits · every year
                </p>
                <div className="mt-8 pt-8 border-t border-white/20">
                  <p className="text-sm text-neutral-300 leading-relaxed">
                    Donated to Norfolk-based wildlife conservation and habitat
                    restoration projects local to where our team works and lives.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
